import {
	LoadingOutlined,
} from "@ant-design/icons";
import { Spin, Checkbox } from "antd";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const CompareCars = () => {
	const location = useLocation();
	const [query] = useSearchParams();
	const isUpdate = query.get("update");
	const navigate = useNavigate();
	const [compareCars, setCompareCars] = useState([]);
	const [checkGenerate, setCheckedGenerate] = useState([]);
	const [loading] = useState(false);
	const [isDisabled, setIsDisabled] = useState(true);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => setIsVisible(true), 100);
		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (location?.state?.CompareCars) {
			if (location?.state?.CompareCars.length === 4) {
				const data = location?.state?.CompareCars;
				localStorage.setItem("compareCars", JSON.stringify(data));
				setCompareCars(data);
			} else {
				const data = [...location?.state?.CompareCars, { emptyData: true }];
				setCompareCars(data);
			}
		} else {
			const storedCars = localStorage.getItem("compareCars");
			if (storedCars) {
				const data = JSON.parse(storedCars);
				if (data.length > 0) {
					setCompareCars([...data, { emptyData: true }]);
				}
			}
		}
	}, [location]);

	useEffect(() => {
		if (checkGenerate.length) {
			setIsDisabled(false);
		} else {
			setIsDisabled(true);
		}
	}, [checkGenerate]);

	const handleRemove = (id) => {
		const filtered = compareCars.filter((item) => {
			return id !== item?.carDetail?.vin;
		});
		setCompareCars(filtered);
		const filterGenerate = filtered
			.filter((item) => {
				return checkGenerate.includes(item?.carDetail?.vin);
			})
			.map((item) => item?.carDetail?.vin);
		setCheckedGenerate(filterGenerate);
		const filterEmptyData = filtered.filter((item) => {
			return !item.emptyData;
		});
		localStorage.setItem("compareCars", JSON.stringify(filterEmptyData));
		if (!filtered[filtered.length - 1].emptyData) {
			setCompareCars([...filtered, { emptyData: true }]);
		} else {
			if (filtered.length === 2) {
				navigate("/find_car", {
					state: {
						filterInput: location?.state?.filters,
						formInputValues: location?.state?.inputFormValues,
					},
				});
			}
		}
	};

	const handleCheckChange = (e, car) => {
		const res = car?.carDetail?.vin;
		if (e.target.checked) {
			setCheckedGenerate([...checkGenerate, res]);
		} else {
			const filtered = checkGenerate.filter((item) => {
				return item !== res;
			});
			setCheckedGenerate(filtered);
		}
	};

	const handleAddToReport = () => {
		const selectedCars = compareCars.filter((item) => {
			return checkGenerate.includes(item?.carDetail?.vin);
		});
		localStorage.setItem("marketCars", JSON.stringify(selectedCars));
		if (isUpdate) {
			navigate("/generatePage");
		} else {
			navigate("/find_salvageCar");
		}
	};

	const handleSelectAll = () => {
		const allVins = compareCars
			.filter((c) => !c.emptyData)
			.map((c) => c?.carDetail?.vin);
		if (checkGenerate.length === allVins.length) {
			setCheckedGenerate([]);
		} else {
			setCheckedGenerate(allVins);
		}
	};

	const totalVehicles = compareCars.filter((c) => !c.emptyData).length;
	const allSelected = totalVehicles > 0 && checkGenerate.length === totalVehicles;

	const DetailRow = ({ label, value }) => (
		<div className="flex justify-between items-center py-2.5 border-b border-slate-700/50 last:border-b-0">
			<span className="text-slate-400 text-sm">{label}</span>
			<span className="text-white font-medium text-sm text-right max-w-[60%] truncate">
				{value || "-"}
			</span>
		</div>
	);

	return (
		<div className="compare-cars-page relative overflow-x-clip">
			{/* Background effects */}
			<div className="absolute inset-0 overflow-hidden pointer-events-none">
				<div className="absolute top-40 -left-32 w-[500px] h-[500px] bg-blue-600/8 rounded-full blur-3xl animate-blob"></div>
				<div className="absolute bottom-40 -right-32 w-[500px] h-[500px] bg-cyan-500/8 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
				<div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-3xl"></div>
			</div>

			{/* Grid overlay */}
			<div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff04_1px,transparent_1px),linear-gradient(to_bottom,#ffffff04_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none"></div>

			<Spin
				spinning={loading}
				indicator={
					<LoadingOutlined
						style={{ fontSize: 48, color: "#38bdf8" }}
						spin
					/>
				}
			>
				{/* Hero Header */}
				<div className={`relative pt-28 pb-10 px-4 text-center transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
					<div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-5">
						<svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
							<path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
						</svg>
						<span className="text-cyan-400 text-sm font-medium">Market Comparables</span>
					</div>
					<h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
						Compare{" "}
						<span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
							Vehicles
						</span>
					</h1>
					<p className="text-slate-400 text-base sm:text-lg max-w-xl mx-auto">
						Review and select vehicles to include in your conditions report
					</p>

					{/* Status bar */}
					{totalVehicles > 0 && (
						<div className="mt-6 flex flex-wrap items-center justify-center gap-3">
							<div className="inline-flex items-center gap-2 bg-slate-800/60 backdrop-blur-sm border border-slate-700/50 rounded-full px-4 py-2">
								<div className="flex gap-1">
									{[...Array(4)].map((_, i) => (
										<div
											key={i}
											className={`w-2 h-2 rounded-full transition-colors duration-300 ${
												i < totalVehicles ? "bg-cyan-400" : "bg-slate-600"
											}`}
										/>
									))}
								</div>
								<span className="text-slate-400 text-sm">
									{totalVehicles} of 4 vehicles
								</span>
								{checkGenerate.length > 0 && (
									<>
										<span className="text-slate-600">|</span>
										<span className="text-cyan-400 text-sm font-medium">
											{checkGenerate.length} selected
										</span>
									</>
								)}
							</div>
							{totalVehicles > 1 && (
								<button
									type="button"
									onClick={handleSelectAll}
									className="text-sm text-slate-400 hover:text-cyan-400 transition-colors duration-200 underline underline-offset-2"
								>
									{allSelected ? "Deselect all" : "Select all"}
								</button>
							)}
						</div>
					)}
				</div>

				{/* Cards Grid */}
				<div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-8">
					<div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
						{compareCars.map((res, i) => (
							<div
								key={res?.carDetail?.vin || `empty-${i}`}
								className={`transition-all duration-500 ${
									isVisible
										? "opacity-100 translate-y-0"
										: "opacity-0 translate-y-8"
								}`}
								style={{ transitionDelay: `${300 + i * 100}ms` }}
							>
								{!compareCars[i].emptyData ? (
									<div
										className={`compare-vehicle-card group ${
											checkGenerate.includes(res?.carDetail?.vin)
												? "ring-2 ring-cyan-500/40 border-cyan-500/30"
												: ""
										}`}
									>
										{/* Image Section */}
										<div className="relative overflow-hidden">
											{res?.carDetail?.photoUrls ? (
												<img
													src={res?.carDetail?.photoUrls[0]}
													alt={`${res?.carDetail?.year} ${res?.carDetail?.make} ${res?.carDetail?.model}`}
													className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
												/>
											) : (
												<div className="w-full h-48 bg-slate-800 flex items-center justify-center">
													<svg className="w-16 h-16 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<rect x="2" y="6" width="20" height="12" rx="2" strokeWidth="1.5" />
														<circle cx="7" cy="18" r="2" strokeWidth="1.5" />
														<circle cx="17" cy="18" r="2" strokeWidth="1.5" />
													</svg>
												</div>
											)}
											{/* Gradient overlay */}
											<div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />

											{/* Card number badge */}
											<div className="absolute top-3 left-3 w-7 h-7 rounded-full bg-slate-900/70 backdrop-blur-sm border border-slate-600/50 flex items-center justify-center">
												<span className="text-slate-300 text-xs font-semibold">{i + 1}</span>
											</div>

											{/* Remove button */}
											<button
												type="button"
												onClick={() => handleRemove(res?.carDetail?.vin)}
												className="absolute top-3 right-3 w-8 h-8 rounded-full bg-red-500/80 hover:bg-red-500 text-white flex items-center justify-center transition-all shadow-lg hover:scale-110 backdrop-blur-sm opacity-0 group-hover:opacity-100"
											>
												<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
													<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
												</svg>
											</button>

											{/* Price badge */}
											{res?.carDetail?.price && (
												<div className="absolute bottom-3 left-3 bg-cyan-500/90 backdrop-blur-sm text-white font-bold px-3 py-1 rounded-lg text-sm shadow-lg">
													{res?.carDetail?.price}
												</div>
											)}
										</div>

										{/* Vehicle Name */}
										<div className="px-4 pt-4 pb-3 border-b border-slate-700/50">
											<h3 className="text-white font-semibold text-base leading-tight">
												{res?.carDetail?.year} {res?.carDetail?.make} {res?.carDetail?.model}
											</h3>
											{res?.carDetail?.trim && (
												<span className="text-cyan-400 text-xs font-medium mt-0.5 inline-block">
													{res?.carDetail?.trim}
												</span>
											)}
										</div>

										{/* Details */}
										<div className="px-4 py-2 flex-1">
											<DetailRow label="VIN" value={res?.carDetail?.vin?.toUpperCase()} />
											<DetailRow label="Year" value={res?.carDetail?.year} />
											<DetailRow label="Make" value={res?.carDetail?.make} />
											<DetailRow label="Model" value={res?.carDetail?.model} />
											<DetailRow label="Trim" value={res?.carDetail?.trim} />
											<DetailRow label="Mileage" value={res?.carDetail?.mileage} />
											<DetailRow label="Dealer" value={res?.carDetail?.dealerName} />
											<DetailRow
												label="Location"
												value={
													res?.carDetail?.city && res?.carDetail?.state
														? `${res?.carDetail?.city}, ${res?.carDetail?.state}`
														: res?.carDetail?.city || res?.carDetail?.state || "-"
												}
											/>
										</div>

										{/* Checkbox */}
										<div className="px-4 py-3 border-t border-slate-700/50">
											<Checkbox
												checked={checkGenerate.includes(res?.carDetail?.vin)}
												className="compare-checkbox"
												onChange={(e) => handleCheckChange(e, res)}
											>
												<span className="text-slate-300 text-sm">Add to Report</span>
											</Checkbox>
										</div>
									</div>
								) : (
									/* Add More Card */
									<button
										type="button"
										className="compare-vehicle-card flex items-center justify-center min-h-[400px] cursor-pointer group w-full"
										onClick={() =>
											navigate("/find_car", {
												state: {
													filterInput: location?.state?.filters,
													formInputValues: location?.state?.inputFormValues,
												},
											})
										}
									>
										<div className="text-center p-6">
											<div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-dashed border-slate-600 group-hover:border-cyan-400/50 group-hover:from-cyan-500/20 group-hover:to-blue-500/20 flex items-center justify-center transition-all duration-300">
												<svg className="w-7 h-7 text-slate-500 group-hover:text-cyan-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
													<path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
												</svg>
											</div>
											<h3 className="text-white font-semibold mb-1">Add Vehicle</h3>
											<p className="text-slate-400 text-sm">Browse and add more cars to compare</p>
										</div>
									</button>
								)}
							</div>
						))}
					</div>
				</div>

				{/* Bottom CTA */}
				<div
					className={`fixed bottom-0 left-0 right-0 z-30 transition-all duration-500 ${
						checkGenerate.length > 0
							? "opacity-100 translate-y-0"
							: "opacity-0 translate-y-4 pointer-events-none"
					}`}
				>
					<div className="bg-slate-900/80 backdrop-blur-xl border-t border-slate-700/50">
						<div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-lg">
									{checkGenerate.length}
								</div>
								<div>
									<p className="text-white font-medium text-sm">
										{checkGenerate.length} vehicle{checkGenerate.length !== 1 ? "s" : ""} selected
									</p>
									<p className="text-slate-400 text-xs">Ready to add to report</p>
								</div>
							</div>
							<button
								className="px-8 py-3 rounded-xl font-semibold text-sm bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:-translate-y-0.5 transition-all duration-300"
								disabled={isDisabled}
								onClick={handleAddToReport}
							>
								Add to Conditions Report
								<svg className="w-4 h-4 inline-block ml-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
									<path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
								</svg>
							</button>
						</div>
					</div>
				</div>

				{/* Static CTA fallback when nothing selected */}
				{checkGenerate.length === 0 && totalVehicles > 0 && (
					<div className="flex justify-center pb-16 px-4">
						<div className="text-center">
							<p className="text-slate-500 text-sm mb-3">Select vehicles above to continue</p>
							<button
								className="px-10 py-4 rounded-xl font-semibold text-base bg-slate-700/50 text-slate-500 cursor-not-allowed"
								disabled
							>
								Add to Conditions Report
							</button>
						</div>
					</div>
				)}

				{/* The bar is fixed, so reserve room for it at the end of the page */}
				{checkGenerate.length > 0 && <div className="h-28" />}

				{/* Spacer when nothing selected */}
				{totalVehicles === 0 && <div className="pb-16" />}
			</Spin>
		</div>
	);
};

export default CompareCars;
